import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import api from "../api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [dashboardData, setDashboardData] = useState([]);
  const [mensagem, setMensagem] = useState("");
  const [selectedIndicador, setSelectedIndicador] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const response = await api.get("/usuarios");
        const usuariosEstado = response.data
          .filter((usuario) => usuario.perfil === "ESTADO")
          .sort((a, b) => {
            const suffixA = a.nome.slice(-2);
            const suffixB = b.nome.slice(-2);
            return suffixA.localeCompare(suffixB);
          });
        setUsuarios(usuariosEstado);
        if (usuariosEstado.length > 0) {
          setSelectedUsuario(usuariosEstado[0].id); // Seleciona o primeiro usuário ordenado
        }
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
      }
    };

    const fetchPerfil = async () => {
      try {
        const response = await api.get("/usuarios/me");
        setIsAdmin(response.data.perfil === "ADMINISTRADOR");
        if (response.data.perfil === "ADMINISTRADOR") {
          fetchUsuarios();
        }
      } catch (error) {
        console.error("Erro ao verificar perfil do usuário:", error);
      }
    };

    fetchPerfil();
  }, []);

  useEffect(() => {
    const fetchDadosDashboard = async () => {
      try {
        const response = await api.get(
          `/lancamentos/dashboard/${ano}` +
            (isAdmin && selectedUsuario
              ? `?usuarioId=${selectedUsuario}`
              : "?usuarioId=1")
        );
        const data = response.data;
        if (data.length === 0) {
          setMensagem("Não há dados registrados no ano selecionado.");
          setDashboardData([]);
        } else {
          setMensagem("");
          setDashboardData(data);
        }
      } catch (error) {
        console.error("Erro ao buscar dados da dashboard:", error);
        setMensagem("Erro ao carregar os dados da dashboard.");
      }
    };

    fetchDadosDashboard();
  }, [ano, selectedUsuario, isAdmin]);

  const anosPermitidos = Array.from(
    { length: hoje.getFullYear() - 2020 + 1 },
    (_, i) => hoje.getFullYear() - i
  );

  const chartData = {
    labels: Array.from({ length: 12 }, (_, i) =>
      new Date(0, i).toLocaleString("pt-BR", { month: "short" })
    ),
    datasets: dashboardData
      .filter((item) => item.descricaoIndicador === selectedIndicador)
      .map((item) => ({
        label: item.descricaoIndicador,
        data: Array.from(
          { length: 12 },
          (_, i) => item.valoresPorMes[i + 1] || 0
        ),
        borderColor: "rgba(75,192,192,1)",
        backgroundColor: "rgba(75,192,192,0.2)",
        fill: true,
      })),
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Typography
        variant="h4"
        mb={3}
        sx={{ textAlign: "center", fontWeight: "bold" }}
      >
        Dashboard de Indicadores
      </Typography>

      {isAdmin && (
        <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
          <InputLabel id="usuario-label">Instituição</InputLabel>
          <Select
            labelId="usuario-label"
            value={selectedUsuario || ""}
            onChange={(e) => setSelectedUsuario(e.target.value)}
            label="Instituição"
          >
            {usuarios.map((usuario) => (
              <MenuItem key={usuario.id} value={usuario.id}>
                {usuario.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
        <InputLabel id="ano-label">Ano</InputLabel>
        <Select
          labelId="ano-label"
          value={ano}
          onChange={(e) => setAno(e.target.value)}
          label="Ano"
        >
          {anosPermitidos.map((anoPermitido) => (
            <MenuItem key={anoPermitido} value={anoPermitido}>
              {anoPermitido}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {mensagem && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {mensagem}
        </Alert>
      )}

      {!mensagem && dashboardData.length > 0 && (
        <>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead className="table-header">
                <TableRow>
                  <TableCell>Indicador</TableCell>
                  {Array.from({ length: 12 }, (_, i) => (
                    <TableCell key={i} align="center">
                      {new Date(0, i).toLocaleString("pt-BR", {
                        month: "short",
                      })}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.map((item) => (
                  <TableRow key={item.descricaoIndicador}>
                    <TableCell>
                      {item.descricaoIndicador} ({item.unidade})
                    </TableCell>
                    {Array.from({ length: 12 }, (_, i) => (
                      <TableCell key={i} align="center">
                        {item.valoresPorMes[i + 1] || "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
            <InputLabel id="indicador-label">Indicador</InputLabel>
            <Select
              labelId="indicador-label"
              value={selectedIndicador}
              onChange={(e) => setSelectedIndicador(e.target.value)}
              label="Indicador"
            >
              {dashboardData.map((item) => (
                <MenuItem
                  key={item.descricaoIndicador}
                  value={item.descricaoIndicador}
                >
                  {item.descricaoIndicador}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedIndicador && (
            <Box>
              <Typography variant="h6" mb={2}>
                Gráfico de {selectedIndicador}
              </Typography>
              <Line data={chartData} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard;
